import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { FlagsContext } from 'src/context/flagsContext';
import { useMediaQuery } from 'src/hooks/useMediaQuery';

export const DRAWER_IDS = {
  INTERNAL_CHAT: 'INTERNAL_CHAT',
  NOTES: 'NOTES',
  DETAILS: 'DETAILS',
} as const;

export type DrawerId = (typeof DRAWER_IDS)[keyof typeof DRAWER_IDS];

// CLEANUP(EnableContextBarVisualUpgrades): Keep these when flag is removed
export const DRAWER_LABELS: Record<DrawerId, string> = {
  [DRAWER_IDS.INTERNAL_CHAT]: 'Internal Chat',
  [DRAWER_IDS.NOTES]: 'Internal Notes',
  [DRAWER_IDS.DETAILS]: 'Client Details',
};

export const MOBILE_DRAWER_LABELS: Record<DrawerId, string> = {
  [DRAWER_IDS.INTERNAL_CHAT]: 'Chat',
  [DRAWER_IDS.NOTES]: 'Notes',
  [DRAWER_IDS.DETAILS]: 'Details',
};

export interface DrawerActionConfig {
  icon: string;
  label: string;
  actionType: 'create_note';
}

export const DRAWER_ACTION_CONFIGS: Partial<
  Record<DrawerId, DrawerActionConfig>
> = {
  [DRAWER_IDS.NOTES]: {
    icon: 'Plus',
    label: 'Create note',
    actionType: 'create_note',
  },
};

export interface ContextBarTarget {
  id: string;
  entityType: 'client' | 'company';
}

export type DrawerVisibilityConfig = Partial<Record<DrawerId, boolean>>;

const DEFAULT_DRAWER_VISIBILITY: Record<DrawerId, boolean> = {
  [DRAWER_IDS.INTERNAL_CHAT]: true,
  [DRAWER_IDS.NOTES]: true,
  [DRAWER_IDS.DETAILS]: true,
};

export interface ContextBarPageTransition {
  target: ContextBarTarget;
  drawerConfig?: DrawerVisibilityConfig;
  open?: boolean;
}

interface ContextBarState {
  target: ContextBarTarget | null;
  drawerVisibilityConfig: Record<DrawerId, boolean>;
  lastOpenDrawer: DrawerId | null;
  drawerHistory: DrawerId[];
  open: boolean;
}

interface ContextBarContextValue extends ContextBarState {
  setTransition: (transition: ContextBarPageTransition) => void;
  setLastOpenDrawer: (drawerId: DrawerId) => void;
  pushDrawerHistory: (drawerId: DrawerId) => void;
  getActiveDrawer: (hasAssistantTab: boolean) => DrawerId;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isMobileLayout: boolean;
}

const defaultContextValue: ContextBarContextValue = {
  target: null,
  drawerVisibilityConfig: DEFAULT_DRAWER_VISIBILITY,
  lastOpenDrawer: null,
  drawerHistory: [],
  open: false,
  setTransition: () => {},
  setLastOpenDrawer: () => {},
  pushDrawerHistory: () => {},
  getActiveDrawer: () => DRAWER_IDS.INTERNAL_CHAT,
  setOpen: () => {},
  isMobileLayout: false,
};

export const ContextBarContext =
  createContext<ContextBarContextValue>(defaultContextValue);

export const ContextBarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [target, setTarget] = useState<ContextBarTarget | null>(null);
  const [drawerVisibilityConfig, setDrawerVisibilityConfig] = useState<
    Record<DrawerId, boolean>
  >(DEFAULT_DRAWER_VISIBILITY);
  const [lastOpenDrawer, setLastOpenDrawerState] = useState<DrawerId | null>(
    null,
  );
  const [drawerHistory, setDrawerHistory] = useState<DrawerId[]>([]);
  const [open, setOpen] = useState(false);
  const { EnableContextBarOtherPage } = useContext(FlagsContext);

  const isMobileLayout = useMediaQuery('sm', 'max');

  const setTransition = useCallback(
    (transition: ContextBarPageTransition) => {
      setTarget(transition.target);
      setDrawerVisibilityConfig({
        ...DEFAULT_DRAWER_VISIBILITY,
        ...transition.drawerConfig,
      });
      if (isMobileLayout) {
        // always start collapsed on mobile, regardless of settings
        setOpen(false);
      } else if (transition.open !== undefined) {
        setOpen(transition.open);
      }
    },
    [isMobileLayout],
  );

  const setLastOpenDrawer = useCallback(
    (drawerId: DrawerId) => {
      if (EnableContextBarOtherPage) {
        setDrawerHistory((prev) => {
          const filtered = prev.filter((id) => id !== drawerId);
          return [drawerId, ...filtered];
        });
      } else {
        setLastOpenDrawerState(drawerId);
      }
    },
    [EnableContextBarOtherPage],
  );

  /**
   * Pushes a drawer id to the front of the history array, deduplicating to keep
   * the array slim. The first entry in the history is the most recently used drawer.
   */
  const pushDrawerHistory = useCallback((drawerId: DrawerId) => {
    setDrawerHistory((prev) => {
      const filtered = prev.filter((id) => id !== drawerId);
      return [drawerId, ...filtered];
    });
  }, []);

  /**
   * Checks if a drawer is valid (visible and has required permissions).
   */
  const isDrawerValid = useCallback(
    (drawerId: DrawerId, hasAssistantTab: boolean): boolean => {
      if (!drawerVisibilityConfig[drawerId]) {
        return false;
      }
      if (drawerId === DRAWER_IDS.INTERNAL_CHAT && !hasAssistantTab) {
        return false;
      }
      return true;
    },
    [drawerVisibilityConfig],
  );

  /**
   * Determines which drawer should be active based on:
   * 1. User's last selected drawer (if still visible)
   * 2. First valid drawer from history
   * 3. Default drawer based on permissions
   */
  const getActiveDrawer = useCallback(
    (hasAssistantTab: boolean): DrawerId => {
      if (EnableContextBarOtherPage) {
        // Check the drawer history for the first valid drawer
        for (const drawerId of drawerHistory) {
          if (isDrawerValid(drawerId, hasAssistantTab)) {
            return drawerId;
          }
        }
      } else {
        // If user has selected a drawer and it's still visible, use it
        if (lastOpenDrawer && isDrawerValid(lastOpenDrawer, hasAssistantTab)) {
          return lastOpenDrawer;
        }
      }

      // Default: INTERNAL_CHAT if available, otherwise NOTES
      return hasAssistantTab ? DRAWER_IDS.INTERNAL_CHAT : DRAWER_IDS.NOTES;
    },
    [EnableContextBarOtherPage, lastOpenDrawer, drawerHistory, isDrawerValid],
  );

  const value = useMemo(
    () =>
      ({
        target,
        drawerVisibilityConfig,
        lastOpenDrawer,
        drawerHistory,
        setTransition,
        setLastOpenDrawer,
        pushDrawerHistory,
        getActiveDrawer,
        open,
        setOpen,
        isMobileLayout,
      }) satisfies ContextBarContextValue,
    [
      target,
      drawerVisibilityConfig,
      lastOpenDrawer,
      drawerHistory,
      setTransition,
      setLastOpenDrawer,
      pushDrawerHistory,
      getActiveDrawer,
      open,
      isMobileLayout,
    ],
  );

  return (
    <ContextBarContext.Provider value={value}>
      {children}
    </ContextBarContext.Provider>
  );
};

export const useContextBarContext = (): ContextBarContextValue => {
  return useContext(ContextBarContext);
};
